import {
  clusterIconSize,
  MARKER_PIN_SIZE,
  MAX_CLUSTER_SIZE,
} from './clusterIcon'
import type { AlertCounts } from './markerStatus'
import type { SimpleMarker } from './SuperClustering'

// Space left between two icons that do not merge.
const GAP_PX = 2
const TILE_PX = 256
// Two icons that overlap are closer than this, so an icon can only overlap
// icons in its own bucket or the 8 buckets around it.
const BUCKET_PX = MAX_CLUSTER_SIZE + GAP_PX

type Placed = {
  x: number
  y: number
  pointCount: number
  alerts?: AlertCounts
  // Kept as is while nothing merges into it.
  marker?: SimpleMarker
}

// The count the icon shows, which sets its size.
const shownTotal = (p: Placed) => p.alerts?.pointCount ?? p.pointCount

const radiusOf = (p: Placed) =>
  (shownTotal(p) > 1 ? clusterIconSize(shownTotal(p)) : MARKER_PIN_SIZE) / 2

const project = ({ lat, lng }: google.maps.LatLngLiteral, world: number) => {
  const sin = Math.sin((lat * Math.PI) / 180)
  return {
    x: ((lng + 180) / 360) * world,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * world,
  }
}

const unproject = (x: number, y: number, world: number) => ({
  lat: (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / world))) * 180) / Math.PI,
  lng: (x / world) * 360 - 180,
})

const overlaps = (a: Placed, b: Placed) =>
  Math.hypot(a.x - b.x, a.y - b.y) < radiusOf(a) + radiusOf(b) + GAP_PX

const bucketKey = (bx: number, by: number) => `${bx}/${by}`
const bucketOf = (p: Placed) =>
  bucketKey(Math.floor(p.x / BUCKET_PX), Math.floor(p.y / BUCKET_PX))

// The icons already placed, by bucket. firstOverlap gives the same answer as
// a scan of the whole list in order, but looks only at nearby icons, so a
// view of thousands of cells stays fast.
class PlacedIcons {
  readonly list: Placed[] = []
  private readonly buckets = new Map<string, number[]>()

  firstOverlap(p: Placed): number {
    const bx = Math.floor(p.x / BUCKET_PX)
    const by = Math.floor(p.y / BUCKET_PX)
    let first = -1
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const i of this.buckets.get(bucketKey(bx + dx, by + dy)) ?? []) {
          if ((first === -1 || i < first) && overlaps(this.list[i], p))
            first = i
        }
      }
    }
    return first
  }

  add(p: Placed) {
    this.list.push(p)
    this.file(this.list.length - 1)
  }

  replace(i: number, p: Placed) {
    const old = this.buckets.get(bucketOf(this.list[i]))!
    old.splice(old.indexOf(i), 1)
    this.list[i] = p
    this.file(i)
  }

  private file(i: number) {
    const key = bucketOf(this.list[i])
    const bucket = this.buckets.get(key)
    if (bucket) bucket.push(i)
    else this.buckets.set(key, [i])
  }
}

const merge = (a: Placed, b: Placed): Placed => {
  const pointCount = a.pointCount + b.pointCount
  return {
    x: (a.x * a.pointCount + b.x * b.pointCount) / pointCount,
    y: (a.y * a.pointCount + b.y * b.pointCount) / pointCount,
    pointCount,
    alerts: a.alerts &&
      b.alerts && {
        pointCount: a.alerts.pointCount + b.alerts.pointCount,
        alertCount: a.alerts.alertCount + b.alerts.alertCount,
      },
  }
}

// Server clusters sit at the centroid of their points, so two of them can be
// drawn closer than their icons are wide. This merges every pair whose icons
// would overlap at this zoom, until none do. A merged icon is bigger and can
// reach a third one, so it repeats. Heaviest first keeps the result the same
// for the same input.
export const separateClusters = (
  items: SimpleMarker[],
  zoom: number
): SimpleMarker[] => {
  const world = TILE_PX * 2 ** Math.round(zoom)
  let placed: Placed[] = items.map(marker => ({
    ...project(marker.position, world),
    pointCount: marker.pointCount ?? 1,
    alerts: marker.alerts,
    marker,
  }))

  let merged = true
  while (merged) {
    merged = false
    placed = placed
      .map((p, order) => ({ p, order }))
      .sort((a, b) => shownTotal(b.p) - shownTotal(a.p) || a.order - b.order)
      .map(({ p }) => p)
    const next = new PlacedIcons()
    for (const p of placed) {
      const hit = next.firstOverlap(p)
      if (hit === -1) {
        next.add(p)
      } else {
        next.replace(hit, merge(next.list[hit], p))
        merged = true
      }
    }
    placed = next.list
  }

  let nextId = Math.min(0, ...items.map(m => m.id)) - 1
  return placed.map(
    p =>
      p.marker ?? {
        id: nextId--,
        name: '',
        address: '',
        position: unproject(p.x, p.y, world),
        pointCount: p.pointCount,
        meterNumber: null,
        alerts: p.alerts,
      }
  )
}
